import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import type { GQLCtx } from '@sorare/core/src/contexts/graphql-search';
import { keyFacetsByApiField } from '@sorare/core/src/contexts/graphql-search';
import { BACKEND_FLASH_SALE_PICKS_FILTERS } from '@sorare/core/src/contexts/graphql-search/backendFlashSalePicksFilters.ts';
import { pickRenderedBackendFilters } from '@sorare/core/src/contexts/graphql-search/pickRenderedBackendFilters.ts';
import { useGQLSearch } from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { useCompetitionEligibilityFilterValue } from '@sorare/core/src/contexts/searchCards/useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from '@sorare/core/src/contexts/searchCards/useFavoriteFilterValue.ts';
import { useFixtureFilterValue } from '@sorare/core/src/contexts/searchCards/useFixtureFilterValue.ts';
import { useWatchlistFilterValue } from '@sorare/core/src/contexts/searchCards/useWatchlistFilterValue.ts';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia.ts';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';

import { toSortInputs } from '#src/hooks/search/useCardsSearchClient.ts';
import { MARKET_CARDS_SEARCH_FACET_VALUES_QUERY } from '#src/hooks/search/useMarketCardsSearch.ts';

type PicksSearchResult = {
  page: number;
  nbPages: number;
  nbHits: number;
  facets: readonly {
    field: string;
    values: readonly { value: string; count: number }[];
    min?: { value: string } | null;
    max?: { value: string } | null;
  }[];
};

type Params = {
  sport: Sport;
  cardFilters: FiltersGroup[];
  sorts: GallerySortNames;
  defaultSort: GallerySortName;
};

// The sport reaches the field as its own argument, so no `sportFilter()` clause joins
// `advancedFilters`, and no default filter set is applied: `useDefaultFilters` injects
// `sale.primary` and `sale.type`, which the field refuses.
export const useBackendFlashSalePicksSearch = ({
  sport,
  cardFilters,
  sorts,
  defaultSort,
}: Params) => {
  const activeBackendCardFilters = useMemo(
    () =>
      pickRenderedBackendFilters(BACKEND_FLASH_SALE_PICKS_FILTERS, cardFilters),
    [cardFilters]
  );

  const { ctxBase, facets, searchParams } = useGQLSearch(
    activeBackendCardFilters
  );
  const { query, page, activeFacets, refinements } = ctxBase;

  // A sort the field cannot serve fails the whole request, and `s` reaches this page from the
  // new-season market tabs, which carry every param across. Anything outside the picks set falls
  // back to the default rather than being forwarded.
  const requestedSort = searchParams.get('s') as GallerySortName | null;
  const currentSortName =
    requestedSort && sorts.includes(requestedSort)
      ? requestedSort
      : defaultSort;

  const favoriteActive = !!activeFacets.ff?.length;
  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteActive });

  const competitionEligibility = activeFacets.lf?.[0];
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(competitionEligibility);

  const gameweekSlug = activeFacets.gw?.[0];
  const gameweekFilterValue = useFixtureFilterValue({
    filterValue: gameweekSlug || undefined,
    eventType: So5FixtureEvent.CLASSIC,
  });

  const watchlistId = activeFacets.wl?.[0];
  const { watchlistFilterValue, loading: watchlistLoading } =
    useWatchlistFilterValue({ filterValue: watchlistId || undefined });

  const advancedFilters = joinFiltersWithAnd(
    [
      favoriteActive && favoriteFilterValue,
      gameweekSlug && gameweekFilterValue,
      competitionEligibility && competitionEligibilityFilterValue,
      watchlistId && watchlistFilterValue,
    ].filter(Boolean) as string[]
  );

  const [search] = useLazyQuery(MARKET_CARDS_SEARCH_FACET_VALUES_QUERY, {
    variables: {
      query,
      advancedFilters,
      refinements,
      field: '',
      facetQuery: '',
      onSaleOnly: false,
    },
  });

  const makeGqlSearchContextValue = (
    result: PicksSearchResult | undefined
  ): GQLCtx => ({
    ...ctxBase,
    search,
    extractFacetValues: searchFacetData =>
      searchFacetData?.searchCardFacetValues,
    pages: result?.nbPages ?? 0,
    nbHits: result?.nbHits ?? 0,
    advancedFilters,
    onSaleOnly: false,
    sorts: [currentSortName],
    facets: keyFacetsByApiField(result?.facets ?? []),
  });

  return {
    query,
    page,
    refinements,
    facets,
    advancedFilters,
    sortInputs: toSortInputs(currentSortName, sport),
    filtersLoading: watchlistLoading,
    makeGqlSearchContextValue,
  };
};
